import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import type { Locale } from '@/i18n/config';
import { ogLocales } from '@/i18n/config';
import { getLocaleFromPath, getAlternatePaths, stripLocaleFromPath } from '@/i18n/routes';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonical?: string;
  structuredData?: object;
  /** Logical path without locale prefix, e.g. /menu */
  logicalPath?: string;
  noindex?: boolean;
}

const baseUrl = 'https://braceesapori.it';

export const SEO = ({
  title,
  description,
  keywords,
  ogImage = '/logo.png',
  ogType = 'website',
  canonical,
  structuredData,
  logicalPath,
  noindex = false,
}: SEOProps) => {
  const location = useLocation();
  const locale: Locale = getLocaleFromPath(location.pathname);
  const logical = logicalPath ?? stripLocaleFromPath(location.pathname);
  const alternates = getAlternatePaths(logical);
  const currentUrl = `${baseUrl}${locale === 'en' ? alternates.en : alternates.it}`;
  const canonicalUrl = canonical || currentUrl;

  useEffect(() => {
    document.title = title;

    const updateMetaTag = (name: string, content: string, attribute: string = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.content = content;
    };

    const updateLinkTag = (rel: string, href: string, hreflang?: string) => {
      const selector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
      let element = document.querySelector(selector) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.rel = rel;
        if (hreflang) element.hreflang = hreflang;
        document.head.appendChild(element);
      }
      element.href = href;
    };

    updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    updateMetaTag('robots', noindex ? 'noindex, follow' : 'index, follow');

    updateMetaTag('og:title', title, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:type', ogType, 'property');
    updateMetaTag('og:url', currentUrl, 'property');
    updateMetaTag('og:image', `${baseUrl}${ogImage}`, 'property');
    updateMetaTag('og:locale', ogLocales[locale], 'property');
    updateMetaTag('og:locale:alternate', locale === 'it' ? ogLocales.en : ogLocales.it, 'property');
    updateMetaTag('og:site_name', 'Brace e Sapori', 'property');

    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', `${baseUrl}${ogImage}`);
    updateMetaTag('twitter:site', '@braceesapori');
    updateMetaTag('twitter:creator', '@braceesapori');

    updateLinkTag('canonical', canonicalUrl);
    updateLinkTag('alternate', `${baseUrl}${alternates.it}`, 'it');
    updateLinkTag('alternate', `${baseUrl}${alternates.en}`, 'en');
    updateLinkTag('alternate', `${baseUrl}${alternates.it}`, 'x-default');

    if (structuredData) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(structuredData);
    }
  }, [title, description, keywords, ogImage, ogType, canonicalUrl, currentUrl, structuredData, locale, alternates, noindex]);

  return null;
};
